package co.uk.paxes.pinpoint.model

/**
 * Hard difficulty AI strategy
 * - Balanced approach: 60% offense, 40% defense
 * - Blocks 6+ point opponent moves
 * - Uses jokers when they provide 2+ point advantage
 * - No randomness - always selects optimal move
 */
class HardStrategy(private val playerId: String) : AIStrategy {
    
    override fun selectMove(
        gameState: GameState,
        availablePins: List<Pin>,
        boardAnalyzer: BoardAnalyzer
    ): Move? {
        val emptyPositions = boardAnalyzer.getEmptyPositions(gameState.board)
        if (emptyPositions.isEmpty() || availablePins.isEmpty()) {
            return null
        }
        
        println("HardStrategy: 💪 HARD AI: Balanced strategy (60% offense, 40% defense)")
        
        val scoreCalculator = ScoreCalculator()
        val allMoves = mutableListOf<Pair<Move, Int>>()
        
        try {
            // Evaluate normal moves with balanced scoring
            for (pin in availablePins) {
                for (position in emptyPositions) {
                    val move = Move(playerId, pin, position)
                    val scoreResult = scoreCalculator.calculateMoveScore(gameState.board, move)
                    val immediateScore = scoreResult.points
                    
                    // Balanced: 60% offense, 40% defense
                    var totalValue = immediateScore * 3 // 60% weight on scoring
                    
                    // Check blocking value (40% weight)
                    for (color in PinColor.values()) {
                        if (color == PinColor.BLACK) continue
                        try {
                            val opponentScore = boardAnalyzer.simulateScore(gameState.board, position, color)
                            if (opponentScore >= 6) {
                                totalValue += opponentScore * 2 // 40% weight on blocking
                            }
                        } catch (e: Exception) {
                            continue
                        }
                    }
                    
                    allMoves.add(Pair(move, totalValue))
                }
            }
            
            // Evaluate JOKER moves (use when they score equal or better)
            val currentPlayer = gameState.currentPlayer
            if (currentPlayer.canUseJoker() && availablePins.isNotEmpty()) {
                println("HardStrategy: 🃏 Evaluating joker moves")
                
                val bestNormalScore = allMoves.filter { !it.first.pin.isJoker }
                    .maxOfOrNull { 
                        val scoreResult = scoreCalculator.calculateMoveScore(gameState.board, it.first)
                        scoreResult.points
                    } ?: 0
                val bestNormalValue = allMoves.filter { !it.first.pin.isJoker }.maxOfOrNull { it.second } ?: 0
                
                for (sacrificePin in availablePins) {
                    for (jokerColor in PinColor.values()) {
                        if (jokerColor == PinColor.BLACK) continue
                        if (jokerColor == sacrificePin.color) continue
                        
                        val jokerPin = Pin.joker(sacrificePin.color, jokerColor)
                        
                        // Find best position for this joker
                        var bestJokerScore = 0
                        var bestJokerPosition: Position? = null
                        for (position in emptyPositions) {
                            val jokerMove = Move(playerId, jokerPin, position)
                            val jokerScoreResult = scoreCalculator.calculateMoveScore(gameState.board, jokerMove)
                            if (jokerScoreResult.points > bestJokerScore) {
                                bestJokerScore = jokerScoreResult.points
                                bestJokerPosition = position
                            }
                        }
                        
                        // Only add joker if it scores > 0
                        if (bestJokerPosition != null && bestJokerScore > 0) {
                            val jokerMove = Move(playerId, jokerPin, bestJokerPosition)
                            var jokerValue = bestJokerScore * 3
                            
                            // CRITICAL FIX: Only apply bonuses if joker scores MORE than best normal move
                            if (bestJokerScore > bestNormalScore) {
                                // Joker scores better - give it decisive advantage
                                val bonusNeeded = (bestNormalValue - jokerValue) + 50
                                jokerValue += bonusNeeded
                                println("HardStrategy:   🃏 Strong joker: ${sacrificePin.color}→$jokerColor = $bestJokerScore points (bonus=$bonusNeeded)")
                            } else if (bestJokerScore == bestNormalScore && bestJokerScore >= 5) {
                                // Equal score and high value - small bonus to prefer joker
                                jokerValue += 20
                                println("HardStrategy:   🃏 Equal joker: ${sacrificePin.color}→$jokerColor = $bestJokerScore points")
                            }
                            // If joker scores LESS than normal move, no bonuses - just base value
                            
                            allMoves.add(Pair(jokerMove, jokerValue))
                        }
                    }
                }
            }
        } catch (e: Exception) {
            println("HardStrategy: Error: ${e.message}")
            val randomPin = availablePins.random()
            val randomPosition = emptyPositions.random()
            return Move(playerId, randomPin, randomPosition)
        }
        
        if (allMoves.isEmpty()) {
            val randomPin = availablePins.random()
            val randomPosition = emptyPositions.random()
            return Move(playerId, randomPin, randomPosition)
        }
        
        // Pick best move
        val sortedMoves = allMoves.sortedByDescending { it.second }
        val bestMove = sortedMoves.first()
        
        println("HardStrategy: 🎯 Best move: ${bestMove.first.pin.color}${if(bestMove.first.pin.isJoker) " (JOKER)" else ""} = ${bestMove.second}")
        
        return bestMove.first
    }
    
    override fun shouldCreateJoker(
        gameState: GameState,
        availablePins: List<Pin>,
        boardAnalyzer: BoardAnalyzer
    ): Boolean {
        // Check current best score without joker
        val scoringOpps = boardAnalyzer.findScoringOpportunities(gameState, availablePins)
        val bestNormalScore = scoringOpps.maxOfOrNull { it.potentialScore } ?: 0
        
        // Evaluate potential joker scores for each color
        var bestJokerScore = 0
        val emptyPositions = boardAnalyzer.getEmptyPositions(gameState.board)
        
        for (color in PinColor.values()) {
            if (color == PinColor.BLACK) continue
            
            for (position in emptyPositions) {
                val score = boardAnalyzer.simulateScore(gameState.board, position, color)
                if (score > bestJokerScore) {
                    bestJokerScore = score
                }
            }
        }
        
        // Create joker if it would score significantly better (2+ points more)
        // OR if it would score 6+ points
        val shouldCreate = (bestJokerScore >= bestNormalScore + 2) || (bestJokerScore >= 6)
        
        if (shouldCreate) {
            println("HardStrategy: Creating joker: bestJoker=$bestJokerScore vs bestNormal=$bestNormalScore")
        }
        
        return shouldCreate
    }
    
    override fun selectJokerColor(
        gameState: GameState,
        boardAnalyzer: BoardAnalyzer
    ): PinColor? {
        // Evaluate each color to find which would score highest
        val emptyPositions = boardAnalyzer.getEmptyPositions(gameState.board)
        var bestColor: PinColor? = null
        var bestScore = 0
        
        for (color in PinColor.values()) {
            if (color == PinColor.BLACK) continue
            
            var colorBestScore = 0
            for (position in emptyPositions) {
                val score = boardAnalyzer.simulateScore(gameState.board, position, color)
                if (score > colorBestScore) {
                    colorBestScore = score
                }
            }
            
            if (colorBestScore > bestScore) {
                bestScore = colorBestScore
                bestColor = color
            }
        }
        
        println("HardStrategy: Selected joker color: $bestColor (would score $bestScore)")
        return bestColor
    }
}
